import React, { useState, useEffect } from "react";
import { Box, Typography } from "@mui/material";
import { Carousel } from "react-responsive-carousel";
import ApiService from "./../../services/api-service";
import Loader from "./../../components/Loader";
import VideoBanner from "./VideoBanner";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import "./index.scss";

// Extract styles
const styles = {
  container: {
    width: "100%",
    padding: "20px 0", // Reduced from 40px
    backgroundColor: "#0d0d0d",
    display: "flex",
    borderRadius: "20px",
    justifyContent: "center",
  },
  innerBox: {
    maxWidth: "1200px",
    width: "100%",
    backgroundColor: "rgba(255, 255, 255, 0.05)",
    padding: "20px", // Reduced from 30px
    borderRadius: "20px",
    boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.8)",
    display: "flex",
    justifyContent: "space-between",
    gap: "20px", // Reduced from 30px
  },
  bannerBox: {
    width: "100%",
    height: "100%",
    borderRadius: "16px",
    overflow: "hidden",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.5)",
    background: "linear-gradient(135deg, rgba(255,255,255,0.08), rgba(0,0,0,0.08))",
    position: "relative",
    flex: 1,
  },
  featuredLabel: {
    position: "absolute",
    top: "10px", // Reduced from 15px
    left: "10px", // Reduced from 15px
    color: "#FFF",
    background: "rgba(0, 0, 0, 0.5)",
    padding: "2px 6px", // Reduced from 4px 8px
    borderRadius: "4px",
    fontWeight: 600,
    fontSize: "0.875rem", // Smaller font
  },
  arrow: {
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    zIndex: 1,
    color: "white",
    cursor: "pointer",
    fontSize: "20px", // Reduced from 24px
  },
  videoBox: {
    width: "100%",
    height: "100%",
    borderRadius: "16px",
    overflow: "hidden",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(135deg, rgba(255,255,255,0.08), rgba(0,0,0,0.08))",
    flex: 1,
    "&:hover": {
      transform: "scale(1.02)",
      transition: "transform 0.3s ease-in-out",
    },
  },
};

// Extract components
const CarouselArrow = ({ direction, onClick }) => (
  <Box
    sx={{ ...styles.arrow, [direction]: "10px" }}
    onClick={onClick}
  >
    {direction === "left" ? "◀" : "▶"}
  </Box>
);

const BannerSlide = ({ banner }) => (
  <Box
    sx={{
      overflow: "hidden",
      borderRadius: "16px",
      "& img": {
        width: "100%",
        height: "100%",
        objectFit: "cover",
        transition: "transform 0.3s ease",
      },
      "&:hover img": {
        transform: "scale(1.05)",
      },
    }}
  >
    <a
      href={banner.url || "#"}
      target="_blank"
      rel="noopener noreferrer"
      style={{ display: "block", width: "100%", height: "100%" }}
    >
      <img src={banner.image?.filePath || ""} alt="Banner" />
    </a>
  </Box>
);

export default function TopBan() {
  const [banner, setBanner] = useState([]);
  const [loading, setLoading] = useState(false);

  // Create test data with colorful placeholders
  const testBanners = [
    { 
      id: 1,
      color: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      title: "Featured Music",
      subtitle: "Discover new artists",
      url: "#"
    },
    { 
      id: 2,
      color: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
      title: "New Releases",
      subtitle: "Fresh tracks daily",
      url: "#"
    },
    { 
      id: 3,
      color: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
      title: "Trending Now",
      subtitle: "What's hot today",
      url: "#"
    },
    { 
      id: 4,
      color: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
      title: "Top Charts",
      subtitle: "Most played songs",
      url: "#"
    },
  ];

  const getBanner = async () => {
    try {
      setLoading(true);
      // Fetch actual banners from API
      const response = await ApiService.GET_MEDIA_BANNERS();
      if (response.result && response.result.data) {
        // Filter out mobile-specific banners for web
        const webBanners = response.result.data.filter(banner => !banner.isMobile);
        setBanner(webBanners);
      } else {
        // Fall back to test banners if no uploaded banners
        setBanner(testBanners);
      }
      setLoading(false);
    } catch (error) {
      console.error("Error fetching banners:", error);
      // Fall back to test banners on error
      setBanner(testBanners);
      setLoading(false);
    }
  };

  useEffect(() => {
    getBanner();
  }, []);

  return (
    <Box sx={styles.container}>
      <Box sx={styles.innerBox}>
        {/* Left Side: Carousel Banner */}
        <Box sx={{
          ...styles.bannerBox,
          minHeight: "250px", // Reduced from 400px
          maxHeight: "250px", // Add max height
        }}>
          <Typography variant="h6" sx={styles.featuredLabel}> {/* Changed from h5 */}
            Featured Content
          </Typography>
          
          {loading ? (
            <Box sx={{ 
              height: "250px", // Reduced from 400px
              display: "flex", 
              alignItems: "center", 
              justifyContent: "center" 
            }}>
              <Loader />
            </Box>
          ) : banner.length > 0 ? (
            <Carousel
              showThumbs={false}
              autoPlay
              interval={6000}
              transitionTime={600}
              infiniteLoop
              showArrows
              showStatus={false}
              renderArrowPrev={(onClick, hasPrev) =>
                hasPrev && <CarouselArrow direction="left" onClick={onClick} />
              }
              renderArrowNext={(onClick, hasNext) =>
                hasNext && <CarouselArrow direction="right" onClick={onClick} />
              }
            >
              {banner.map((b, index) => (
                <Box
                  key={b.uniqueId || b.id || `banner-${index}`}
                  component={b.url ? "a" : "div"}
                  href={b.url || "#"}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    height: "250px", // Reduced from 400px
                    background: b.color || "transparent",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                    borderRadius: "16px",
                    position: "relative",
                    overflow: "hidden",
                    cursor: "pointer",
                    textDecoration: "none",
                    "&:hover": {
                      "& .banner-content": {
                        transform: "translateY(-5px)", // Reduced from -10px
                      }
                    }
                  }}
                >
                  {b.image?.filePath ? (
                    // Check if it's a video or image
                    b.image.filePath.endsWith(".mp4") || 
                    b.image.filePath.endsWith(".webm") || 
                    b.image.filePath.endsWith(".mov") ? (
                      <video
                        src={b.image.filePath}
                        autoPlay
                        loop
                        muted
                        playsInline
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          position: "absolute",
                          top: 0,
                          left: 0,
                        }}
                      />
                    ) : (
                      <img 
                        src={b.image.filePath} 
                        alt="Banner" 
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          position: "absolute",
                          top: 0,
                          left: 0,
                        }}
                      />
                    )
                  ) : (
                    <Box 
                      className="banner-content"
                      sx={{ 
                        zIndex: 1, 
                        textAlign: "center",
                        transition: "transform 0.3s ease",
                      }}
                    >
                      <Typography 
                        variant="h4" // Reduced from h3
                        sx={{ 
                          fontWeight: "bold", 
                          mb: 1, // Reduced from 2
                          textShadow: "2px 2px 4px rgba(0,0,0,0.3)"
                        }}
                      >
                        {b.title}
                      </Typography>
                      <Typography 
                        variant="body1" // Reduced from h6
                        sx={{ 
                          opacity: 0.9,
                          textShadow: "1px 1px 2px rgba(0,0,0,0.3)"
                        }}
                      >
                        {b.subtitle}
                      </Typography>
                    </Box>
                  )}
                </Box>
              ))}
            </Carousel>
          ) : (
            <Box
              sx={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                height: "250px", // Reduced from 400px
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "white",
                borderRadius: "16px",
              }}
            >
              <Typography variant="h5" sx={{ fontWeight: "bold" }}> {/* Reduced from h4 */}
                Coming Soon
              </Typography>
            </Box>
          )}
        </Box>

        {/* Right Side: Video Banner */}
        <Box sx={{
          ...styles.videoBox,
          minHeight: "250px", // Reduced from 400px
          maxHeight: "250px", // Add max height
        }}>
          <VideoBanner />
        </Box>
      </Box>
    </Box>
  );
}